import React, { useState, useCallback, useEffect, useRef } from "react";
import { debounce } from "lodash";
import axiosInstance from "../../utils/axiosConfig";

const AddressInput = ({ 
  value, 
  onChange, 
  onSelect, 
  placeholder = "Start typing an address...", 
  className = "",
  name = "",
  rows = 3,
  styleType = "default" // Can be "default" (freight) or "crm"
}) => {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  // An answer that arrives after the person has left the box must not open
  // the list again over whatever they moved on to (seen on Pit to Port: the
  // first row's suggestions covered the second row).
  const focused = useRef(false);

  // Sync internal state with external value prop
  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  const searchAddresses = useCallback(
    debounce(async (text) => {
      if (text.length < 3) {
        setSuggestions([]);
        setShowDropdown(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await axiosInstance.get(`/api/globe/address-autocomplete/?q=${encodeURIComponent(text)}`);
        setSuggestions(response.data);
        setShowDropdown(focused.current && response.data.length > 0);
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
        setSuggestions([]);
        setShowDropdown(false);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    
    // Call the parent onChange handler
    if (onChange) {
      onChange(e);
    }
    
    // Trigger address search
    searchAddresses(newValue);
  };

  const handleSuggestionSelect = (suggestion) => {
    setQuery(suggestion.label);
    setSuggestions([]);
    setShowDropdown(false);

    // onSelect owns the full address update (including coordinates).
    // Do not also call onChange — it races with stale parent state and strips coords.
    if (onSelect) {
      onSelect(suggestion);
      return;
    }

    if (onChange) {
      onChange({
        target: {
          name: name,
          value: suggestion.label
        }
      });
    }
  };

  const handleBlur = () => {
    focused.current = false;
    // Delay hiding dropdown to allow for click events
    setTimeout(() => {
      setShowDropdown(false);
    }, 200);
  };

  const handleFocus = () => {
    focused.current = true;
    if (suggestions.length > 0) {
      setShowDropdown(true);
    }
  };

  // Determine the textarea class based on styleType
  const textareaClass = styleType === "crm" ? "crm-address-textarea" : "freight-recap-textarea";
  const containerClass = styleType === "crm" ? "crm-address-input-container" : "address-input-container";
  const dropdownClass = styleType === "crm" ? "crm-address-suggestions-dropdown" : "address-suggestions-dropdown";

  return (
    <div className={`${containerClass} ${className}`}>
      <textarea
        name={name}
        value={query}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        rows={rows}
        className={textareaClass}
      />
      
      {showDropdown && (
        <ul className={dropdownClass}>
          {isLoading && (
            <li className="loading">
              Loading addresses...
            </li>
          )}
          {!isLoading && suggestions.map((suggestion, index) => (
            <li
              key={index}
              onMouseDown={() => handleSuggestionSelect(suggestion)}
            >
              {suggestion.label}
            </li>
          ))}
          {!isLoading && suggestions.length === 0 && query.length >= 3 && (
            <li className="no-results">
              No addresses found
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default AddressInput; 